import { jsx, Fragment, jsxs } from "react/jsx-runtime";
import * as React from "react";
import { createContext, useId, useRef, useContext, useInsertionEffect, useCallback, useMemo, useEffect, Children, isValidElement, useLayoutEffect, useState } from "react";
import classNames from "classnames";
import { ZIndex, KeyboardEventKey, AnalyticsId } from "./constants.js";
import { useFocusTrap } from "./hooks/useFocusTrap.js";
import { useIsMobileBreakpoint } from "./hooks/useIsMobileBreakpoint.js";
import { useKeyboardEvent } from "./hooks/useKeyboardEvent.js";
import { useOutsideEvent } from "./hooks/useOutsideEvent.js";
import { useReturnFocusOnUnmount } from "./hooks/useReturnFocusOnUnmount.js";
import { getAriaLabelAttributes } from "./utils/accessibility.js";
import { u as uuid } from "./uuid.js";
import { B as Button } from "./Button.js";
import { C as Close } from "./Close.js";
import { T as Title } from "./Title.js";
import styles from "./components/Drawer/styles.module.scss";
import { a as useConstant, i as isBrowser, u as useAnimate } from "./use-animate.js";
const LayoutGroupContext = createContext({});
const PresenceContext = createContext(null);
const MotionConfigContext = createContext({
  transformPagePoint: (p) => p,
  isStatic: false,
  reducedMotion: "never"
});
class PopChildMeasure extends React.Component {
  getSnapshotBeforeUpdate(prevProps) {
    const element = this.props.childRef.current;
    if (element && prevProps.isPresent && !this.props.isPresent) {
      const size = this.props.sizeRef.current;
      size.height = element.offsetHeight || 0;
      size.width = element.offsetWidth || 0;
      size.top = element.offsetTop;
      size.left = element.offsetLeft;
    }
    return null;
  }
  /**
   * Required with getSnapshotBeforeUpdate to stop React complaining.
   */
  componentDidUpdate() {
  }
  render() {
    return this.props.children;
  }
}
function PopChild({ children, isPresent }) {
  const id = useId();
  const ref = useRef(null);
  const size = useRef({
    width: 0,
    height: 0,
    top: 0,
    left: 0
  });
  const { nonce } = useContext(MotionConfigContext);
  useInsertionEffect(() => {
    const { width, height, top, left } = size.current;
    if (isPresent || !ref.current || !width || !height)
      return;
    ref.current.dataset.motionPopId = id;
    const style = document.createElement("style");
    if (nonce)
      style.nonce = nonce;
    document.head.appendChild(style);
    if (style.sheet) {
      style.sheet.insertRule(`
          [data-motion-pop-id="${id}"] {
            position: absolute !important;
            width: ${width}px !important;
            height: ${height}px !important;
            top: ${top}px !important;
            left: ${left}px !important;
          }
        `);
    }
    return () => {
      document.head.removeChild(style);
    };
  }, [isPresent]);
  return jsx(PopChildMeasure, { isPresent, childRef: ref, sizeRef: size, children: React.cloneElement(children, { ref }) });
}
const PresenceChild = ({ children, initial, isPresent, onExitComplete, custom, presenceAffectsLayout, mode }) => {
  const presenceChildren = useConstant(newChildrenMap);
  const id = useId();
  const memoizedOnExitComplete = useCallback((childId) => {
    presenceChildren.set(childId, true);
    for (const isComplete of presenceChildren.values()) {
      if (!isComplete)
        return;
    }
    onExitComplete && onExitComplete();
  }, [presenceChildren, onExitComplete]);
  const context = useMemo(
    () => ({
      id,
      initial,
      isPresent,
      custom,
      onExitComplete: memoizedOnExitComplete,
      register: (childId) => {
        presenceChildren.set(childId, false);
        return () => presenceChildren.delete(childId);
      }
    }),
    /**
     * If the presence of a child affects the layout of the components around it,
     * we want to make a new context value to ensure they get re-rendered
     * so they can detect that layout change.
     */
    presenceAffectsLayout ? [Math.random(), memoizedOnExitComplete] : [isPresent, memoizedOnExitComplete]
  );
  useMemo(() => {
    presenceChildren.forEach((_, key) => presenceChildren.set(key, false));
  }, [isPresent]);
  React.useEffect(() => {
    !isPresent && !presenceChildren.size && onExitComplete && onExitComplete();
  }, [isPresent]);
  if (mode === "popLayout") {
    children = jsx(PopChild, { isPresent, children });
  }
  return jsx(PresenceContext.Provider, { value: context, children });
};
function newChildrenMap() {
  return /* @__PURE__ */ new Map();
}
function usePresence(subscribe = true) {
  const context = useContext(PresenceContext);
  if (context === null)
    return [true, null];
  const { isPresent, onExitComplete, register } = context;
  const id = useId();
  useEffect(() => {
    if (subscribe)
      register(id);
  }, [subscribe]);
  const safeToRemove = useCallback(() => subscribe && onExitComplete && onExitComplete(id), [id, onExitComplete, subscribe]);
  return !isPresent && onExitComplete ? [false, safeToRemove] : [true];
}
const getChildKey = (child) => child.key || "";
function onlyElements(children) {
  const filtered = [];
  Children.forEach(children, (child) => {
    if (isValidElement(child))
      filtered.push(child);
  });
  return filtered;
}
const useIsomorphicLayoutEffect = isBrowser ? useLayoutEffect : useEffect;
const AnimatePresence = ({ children, custom, initial = true, onExitComplete, presenceAffectsLayout = true, mode = "sync", propagate = false }) => {
  const [isParentPresent, safeToRemove] = usePresence(propagate);
  const presentChildren = useMemo(() => onlyElements(children), [children]);
  const presentKeys = propagate && !isParentPresent ? [] : presentChildren.map(getChildKey);
  const isInitialRender = useRef(true);
  const pendingPresentChildren = useRef(presentChildren);
  const exitComplete = useConstant(() => /* @__PURE__ */ new Map());
  const [diffedChildren, setDiffedChildren] = useState(presentChildren);
  const [renderedChildren, setRenderedChildren] = useState(presentChildren);
  useIsomorphicLayoutEffect(() => {
    isInitialRender.current = false;
    pendingPresentChildren.current = presentChildren;
    for (let i = 0; i < renderedChildren.length; i++) {
      const key = getChildKey(renderedChildren[i]);
      if (!presentKeys.includes(key)) {
        if (exitComplete.get(key) !== true) {
          exitComplete.set(key, false);
        }
      } else {
        exitComplete.delete(key);
      }
    }
  }, [renderedChildren, presentKeys.length, presentKeys.join("-")]);
  const exitingChildren = [];
  if (presentChildren !== diffedChildren) {
    let nextChildren = [...presentChildren];
    for (let i = 0; i < renderedChildren.length; i++) {
      const child = renderedChildren[i];
      const key = getChildKey(child);
      if (!presentKeys.includes(key)) {
        nextChildren.splice(i, 0, child);
        exitingChildren.push(child);
      }
    }
    if (mode === "wait" && exitingChildren.length) {
      nextChildren = exitingChildren;
    }
    setRenderedChildren(onlyElements(nextChildren));
    setDiffedChildren(presentChildren);
    return;
  }
  if (process.env.NODE_ENV !== "production" && mode === "wait" && renderedChildren.length > 1) {
    console.warn(`You're attempting to animate multiple children within AnimatePresence, but its mode is set to "wait". This will lead to odd visual behaviour.`);
  }
  const { forceRender } = useContext(LayoutGroupContext);
  return jsx(Fragment, { children: renderedChildren.map((child) => {
    const key = getChildKey(child);
    const isPresent = propagate && !isParentPresent ? false : presentChildren === renderedChildren || presentKeys.includes(key);
    const onExit = () => {
      if (exitComplete.has(key)) {
        exitComplete.set(key, true);
      } else {
        return;
      }
      let isEveryExitComplete = true;
      exitComplete.forEach((isExitComplete) => {
        if (!isExitComplete)
          isEveryExitComplete = false;
      });
      if (isEveryExitComplete) {
        forceRender === null || forceRender === void 0 ? void 0 : forceRender();
        setRenderedChildren(pendingPresentChildren.current);
        propagate && (safeToRemove === null || safeToRemove === void 0 ? void 0 : safeToRemove());
        onExitComplete && onExitComplete();
      }
    };
    return jsx(PresenceChild, { isPresent, initial: !isInitialRender.current || initial ? void 0 : false, custom: isPresent ? void 0 : custom, presenceAffectsLayout, mode, onExitComplete: isPresent ? void 0 : onExit, children: child }, key);
  }) });
};
const Drawer = (props) => {
  const { isOpen, ...rest } = props;
  return /* @__PURE__ */ jsx(AnimatePresence, { children: isOpen && /* @__PURE__ */ jsx(InnerDrawer, { ...rest }) });
};
const InnerDrawer = (props) => {
  const {
    ariaLabel,
    ariaLabelledBy,
    ariaLabelCloseBtn,
    children,
    closeColor = "blueberry",
    desktopDirection = "left",
    footerContent,
    headerClasses,
    onPrimaryAction,
    onRequestClose,
    onSecondaryAction,
    primaryActionText,
    secondaryActionText,
    title,
    titleHtmlMarkup = "h3",
    titleId = uuid(),
    zIndex = ZIndex.OverlayScreen
  } = props;
  const ariaLabelAttributes = getAriaLabelAttributes({ label: ariaLabel, id: ariaLabelledBy, fallbackId: titleId });
  const overlayRef = useRef(null);
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const isMobile = useIsMobileBreakpoint();
  const [scope, animate] = useAnimate();
  const [isPresent, safeToRemove] = usePresence();
  const contentIsScrollable = contentRef.current && contentRef.current.scrollHeight > contentRef.current.clientHeight;
  const headerStyling = classNames(styles.drawer__header, headerClasses);
  const hasFooterContent = typeof footerContent !== "undefined" && footerContent || onPrimaryAction || onSecondaryAction;
  useFocusTrap(containerRef, true);
  useReturnFocusOnUnmount(containerRef);
  useOutsideEvent(containerRef, () => {
    onRequestClose && onRequestClose();
  });
  useKeyboardEvent(containerRef, () => onRequestClose && onRequestClose(), [KeyboardEventKey.Escape]);
  useEffect(() => {
    var _a;
    (_a = containerRef.current) == null ? void 0 : _a.focus();
  }, []);
  useEffect(() => {
    if (!overlayRef.current || !containerRef.current) return;
    if (!isPresent) {
      closeDrawer();
      return;
    }
    if (isMobile) {
      animate(containerRef.current, { y: "0" }, { duration: 0.3, ease: "easeInOut" });
    } else {
      animate(containerRef.current, { x: "0" }, { duration: 0.3, ease: "easeInOut" });
    }
    animate(overlayRef.current, { opacity: 1, pointerEvents: "auto" }, { duration: 0.3, ease: "easeInOut" });
  }, [isPresent]);
  useEffect(() => {
  }, [isPresent]);
  const closeDrawer = () => {
    if (!overlayRef.current || !containerRef.current) return;
    animate(overlayRef.current, { opacity: 0, pointerEvents: "none" }, { duration: 0.3, ease: "easeInOut" });
    if (isMobile) {
      animate(
        containerRef.current,
        { y: "100%" },
        {
          duration: 0.3,
          ease: "easeInOut",
          onComplete: () => {
            safeToRemove && safeToRemove();
          }
        }
      );
    } else {
      animate(
        containerRef.current,
        { x: desktopDirection === "left" ? "-100%" : "100%" },
        {
          duration: 0.3,
          ease: "easeInOut",
          onComplete: () => {
            safeToRemove && safeToRemove();
          }
        }
      );
    }
  };
  const handleCTA = (callback) => {
    if (callback) {
      callback();
    }
  };
  return /* @__PURE__ */ jsxs("div", { className: styles.drawer, ref: scope, style: { zIndex }, "data-analyticsid": AnalyticsId.Drawer, children: [
    /* @__PURE__ */ jsx("div", { className: styles.drawer__overlay, ref: overlayRef, "aria-hidden": "true" }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: classNames(styles.drawer__container, {
          [styles["drawer__container--right"]]: desktopDirection === "right"
        }),
        ref: containerRef,
        role: "dialog",
        "aria-modal": "true",
        tabIndex: -1,
        ...ariaLabelAttributes,
        children: [
          /* @__PURE__ */ jsxs("div", { className: styles.drawer__container__inner, children: [
            /* @__PURE__ */ jsxs("div", { className: headerStyling, children: [
              /* @__PURE__ */ jsx(Title, { id: ariaLabelAttributes == null ? void 0 : ariaLabelAttributes["aria-labelledby"], htmlMarkup: titleHtmlMarkup, appearance: "title3", children: title }),
              !props.noCloseButton && /* @__PURE__ */ jsx(
                Close,
                {
                  ariaLabel: ariaLabelCloseBtn,
                  color: closeColor,
                  onClick: onRequestClose,
                  className: styles["drawer__header__close-button"]
                }
              )
            ] }),
            /* @__PURE__ */ jsx(
              "div",
              {
                className: styles.drawer__content,
                tabIndex: contentIsScrollable ? 0 : void 0,
                role: contentIsScrollable ? "region" : void 0,
                ...contentIsScrollable ? ariaLabelAttributes : {},
                ref: contentRef,
                children
              }
            )
          ] }),
          hasFooterContent && /* @__PURE__ */ jsx("div", { className: styles.drawer__footer, children: footerContent ? footerContent : /* @__PURE__ */ jsxs(Fragment, { children: [
            onPrimaryAction && /* @__PURE__ */ jsx(Button, { onClick: () => handleCTA(onPrimaryAction), children: primaryActionText }),
            onSecondaryAction && /* @__PURE__ */ jsx(Button, { variant: "borderless", onClick: () => handleCTA(onSecondaryAction), children: secondaryActionText })
          ] }) })
        ]
      }
    )
  ] });
};
export {
  Drawer as D
};
//# sourceMappingURL=Drawer.js.map
